/// <reference types="chartjs-plugin-annotation" />
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
    type ChartData,
    type ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { useLocale } from 'next-intl';
import { Bar } from 'react-chartjs-2';
import { LocationStoryblok } from '../../../types/storyblok/storyblok-types';

import { localizePriceCurrency } from '@/utils/storyblok';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);

export default function HorizontalRangeChart({
    location,
    investmentAmount,
    exchangeRate,
    currency,
}: {
    location: LocationStoryblok;
    investmentAmount: number;
    currency: string;
    exchangeRate: number;
}) {
    const COLOR_PALETTE = [
        'rgba(99,102,241,0.85)', // indigo‑500
        'rgba(16,185,129,0.85)', // emerald‑500
        'rgba(234,179,8,0.85)', // amber‑500
        'rgba(239,68,68,0.85)', // red‑500
        'rgba(59,130,246,0.85)', // blue‑500
        'rgba(251,191,36,0.85)', // yellow‑400
        'rgba(132,204,22,0.85)', // lime‑500
    ];
    const getColor = (index: number) => COLOR_PALETTE[index % COLOR_PALETTE.length];

    const locale = useLocale();
    const ranges = (location?.types ?? []).map(t => {
        const starting = Number(t.startingPriceUsd) * exchangeRate;
        const ending = Number(t.endingPriceUsd) * exchangeRate;

        const discount = t.isOffPlan && t.discountOffPlanPercentage ? Number(t.discountOffPlanPercentage) / 100 : 0;

        return {
            name: `${t.name} ${t.isOffPlan ? ' - Off Plan' : ''} `,
            from: starting - starting * discount,
            to: ending - ending * discount,
        };
    });

    const spacerDataset = {
        label: 'spacer',
        data: ranges.map(r => r.from),
        backgroundColor: 'rgba(0,0,0,0)', // invisible
        borderWidth: 0,
        stack: 'rangeStack',
    };

    const rangeDataset = {
        label: 'price',
        data: ranges.map(r => r.to - r.from),
        backgroundColor: ranges.map((_, i) => getColor(i)),
        borderRadius: 8,
        stack: 'rangeStack',
        borderSkipped: false,
    };

    const data: ChartData<'bar', number[], string> = {
        labels: ranges.map(r => r.name),
        datasets: [spacerDataset, rangeDataset],
    };
    const options: ChartOptions<'bar'> = {
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
            legend: {
                display: false,
                labels: { color: '#ffffff' },
            },
            tooltip: {
                callbacks: {
                    label(ctx) {
                        const index = ctx.dataIndex;
                        const range = ranges[index];
                        return [
                            `from: ${
                                localizePriceCurrency(range.from, String(currency), String(currency), locale, {
                                    exchangeRate,
                                }).default
                            } to: ${
                                localizePriceCurrency(range.to, String(currency), String(currency), locale, {
                                    exchangeRate,
                                }).default
                            }`,
                        ];
                    },
                },
            },
            annotation: {
                annotations: {
                    investmentLine: {
                        type: 'line',
                        xMin: investmentAmount * exchangeRate,
                        xMax: investmentAmount * exchangeRate,
                        borderColor: 'red',
                        borderWidth: 2,
                        label: {
                            // display: true,
                            position: 'center',
                            content: 'your investment',
                            color: '#ffffff',
                            z: -1,
                            backgroundColor: 'red',
                        },
                    },
                },
            },
        },

        scales: {
            x: {
                stacked: true,
                beginAtZero: true,

                ticks: {
                    color: '#ffffff',
                    callback: v =>
                        localizePriceCurrency(Number(v), String(currency), String(currency), locale, {
                            exchangeRate,
                        }).default,
                },

                grid: {
                    color: '#ffffff',
                },
            },

            y: {
                stacked: true,

                ticks: {
                    color: '#ffffff',
                    align: 'start',
                    callback(val: string) {
                        return val.length > 20 ? `${val.slice(0, 20)}…` : val + 1;
                    },
                },
            },
        },
    };

    return (
        <div
            style={{
                height: '100%',
            }}
        >
            <Bar
                data={data}
                options={options}
            />
        </div>
    );
}
